import React, { createContext, useContext, useMemo } from 'react';
import { ContentfulSnackbar } from '@jbhi-fi/apis/src/contentful/types';
import { SnackbarTransformer } from '@jbhi-fi/components/src/Contentful/transformers/SnackbarTransformer';
import { getStoreLocation } from '../../../../utils/location/locationHelper';
import { useLocalisationContext } from '../../localisation/context/LocalisationContext';
import { StoreLocation } from '../../localisation/utils/types';
import resolveResponse from 'contentful-resolve-response';

type SnackbarContextType = {
  snackbarProps: ReturnType<typeof SnackbarTransformer.toSnackbarProps> | undefined;
};

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

export const useSnackbarContext = (): SnackbarContextType => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbarContext must be used within a SnackbarProvider');
  }
  return context;
};

const getSnackbarDataForStore = (
  snackbars: Array<ContentfulSnackbar>,
  selectedStore: StoreLocation
):
  | { main: Array<ContentfulSnackbar>; sub: Array<ContentfulSnackbar>; countdown: Array<ContentfulSnackbar> }
  | undefined => {
  try {
    const currentPage = window.location.pathname.substring(1);
    const storeCode = getStoreLocation(selectedStore);
    // Filter and group the snackbar data for pages
    const groupedSnackbarData = snackbars.reduce(
      (
        acc: { main: Array<ContentfulSnackbar>; sub: Array<ContentfulSnackbar>; countdown: Array<ContentfulSnackbar> },
        item: ContentfulSnackbar
      ) => {
        if (
          (item?.fields?.paths?.includes(currentPage) ||
            !item.fields?.paths ||
            (currentPage === '' && item?.fields?.paths?.includes('homepage'))) &&
          (storeCode === '' || !item.fields?.state || item.fields?.state?.includes(storeCode))
        ) {
          if (item?.fields?.type === 'Main') {
            acc.main.push(item);
          } else if (item?.fields?.type === 'Sub') {
            acc.sub.push(item);
          } else if (item?.fields?.type === 'Countdown') {
            acc.countdown.push(item);
          }
        }
        return acc;
      },
      { main: [], sub: [], countdown: [] }
    );

    return groupedSnackbarData;
  } catch (err) {
    console.error('Error parsing snackbar data', err);
    return undefined;
  }
};

export const SnackbarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { selectedStore } = useLocalisationContext();

  const snackbarProps = useMemo(() => {
    const snackbarEntries = JSON.parse(decodeURIComponent(window?.Jbhifi?.snackbarData ?? '') || '{}');
    if (!snackbarEntries?.items?.length) return;
    snackbarEntries.includes.asset = snackbarEntries.includes.asset || [];
    const resolvedSnackbars = resolveResponse(snackbarEntries);
    const snackbarData = getSnackbarDataForStore(resolvedSnackbars ?? [], selectedStore);
    const snackbarProps = snackbarData ? SnackbarTransformer.toSnackbarProps(snackbarData) : undefined;
    return snackbarProps;
  }, [selectedStore]);

  return <SnackbarContext.Provider value={{ snackbarProps }}>{children}</SnackbarContext.Provider>;
};
